import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, Apple, Smartphone, Check, X } from "lucide-react";

/**
 * PWA Install component — handles:
 *  - Android/Chrome native "beforeinstallprompt" install
 *  - iOS Safari "Add to Home Screen" instructions modal
 *  - Hides if app is already installed (display-mode standalone)
 */
export default function InstallApp({ variant = "card" }) {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);

  useEffect(() => {
    // Check if already installed (running in standalone mode)
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    setIsInstalled(standalone);

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);

    const installedHandler = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener("appinstalled", installedHandler);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installedHandler);
    };
  }, []);

  const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isAndroid = /Android/.test(navigator.userAgent);
  const isMobile = isIos || isAndroid;

  if (isInstalled && variant === "button") {
    return (
      <div className="inline-flex items-center gap-2 text-sm text-[#22C55E] font-semibold" data-testid="installed-badge">
        <Check size={16}/> {t("installApp.installedBadge")}
      </div>
    );
  }

  const install = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === "accepted") setIsInstalled(true);
      setDeferredPrompt(null);
      return;
    }
    if (isIos) {
      setShowIosHelp(true);
      return;
    }
    setShowIosHelp(true);
  };

  if (variant === "button") {
    return (
      <>
        <button onClick={install} className="btn-ink flex items-center gap-2" data-testid="install-app-btn">
          <Download size={18}/> {t("installApp.installBtn")}
        </button>
        {showIosHelp && <IosInstructions onClose={()=>setShowIosHelp(false)} isIos={isIos} isAndroid={isAndroid}/>}
      </>
    );
  }

  // Card variant — full landing section
  return (
    <>
      <div className="bg-[#111C30] text-white p-8 lg:p-12 relative overflow-hidden">
        <div className="absolute top-8 right-8 w-32 h-32 bg-[#00BFFF] hidden lg:block"/>
        <div className="relative max-w-3xl">
          <div className="overline" style={{color: "#FBBF24"}}>{t("installApp.badge")}</div>
          <h2 className="font-display text-3xl lg:text-5xl font-extrabold tracking-tight mt-4">
            {t("installApp.cardTitle")}
          </h2>
          <p className="mt-5 text-white/70 text-lg max-w-2xl">
            {t("installApp.cardDesc")}
          </p>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#0B111D]/10">
            <div className="bg-[#111C30] p-5">
              <Check size={20} className="text-[#FBBF24]"/>
              <div className="font-display font-bold mt-3">{t("installApp.feat1Title")}</div>
              <div className="text-sm text-white/60 mt-1">{t("installApp.feat1Desc")}</div>
            </div>
            <div className="bg-[#111C30] p-5">
              <Check size={20} className="text-[#FBBF24]"/>
              <div className="font-display font-bold mt-3">{t("installApp.feat2Title")}</div>
              <div className="text-sm text-white/60 mt-1">{t("installApp.feat2Desc")}</div>
            </div>
            <div className="bg-[#111C30] p-5">
              <Check size={20} className="text-[#FBBF24]"/>
              <div className="font-display font-bold mt-3">{t("installApp.feat3Title")}</div>
              <div className="text-sm text-white/60 mt-1">{t("installApp.feat3Desc")}</div>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <button onClick={install} data-testid="install-cta-android" className="bg-[#00BFFF] hover:bg-[#00A6DD] text-white font-semibold px-5 py-3 flex items-center gap-3 transition-colors">
              <Smartphone size={20}/>
              <div className="text-left">
                <div className="text-xs text-white/80 leading-none">{t("installApp.androidLabel")}</div>
                <div className="text-sm font-bold leading-tight">{t("installApp.androidAction")}</div>
              </div>
            </button>
            <button onClick={()=>setShowIosHelp(true)} data-testid="install-cta-ios" className="bg-[#0B111D] text-[#E2E8F0] hover:bg-[#0B111D]/90 font-semibold px-5 py-3 flex items-center gap-3 transition-colors">
              <Apple size={20}/>
              <div className="text-left">
                <div className="text-xs text-[#94A3B8] leading-none">{t("installApp.iosLabel")}</div>
                <div className="text-sm font-bold leading-tight">{t("installApp.iosAction")}</div>
              </div>
            </button>
          </div>

          {!isMobile && (
            <p className="mt-6 text-sm text-white/60">
              {t("installApp.desktopNote")}
            </p>
          )}

          <div className="mt-6 bg-[#FBBF24]/10 border-l-2 border-[#FBBF24] p-4 text-sm">
            <div className="font-display font-bold text-[#FBBF24]">{t("installApp.androidImportantTitle")}</div>
            <div className="text-white/80 mt-1">
              {t("installApp.androidImportantDesc")}
            </div>
          </div>
        </div>
      </div>
      {showIosHelp && <IosInstructions onClose={()=>setShowIosHelp(false)} isIos={isIos} isAndroid={isAndroid}/>}
    </>
  );
}

function IosInstructions({ onClose, isIos, isAndroid }) {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end md:items-center justify-center p-0 md:p-4" onClick={onClose} data-testid="install-help-modal">
      <div onClick={(e)=>e.stopPropagation()} className="bg-[#0B111D] w-full md:max-w-md border border-[#1E293B]">
        <div className="flex items-center justify-between p-5 border-b border-[#1E293B]">
          <div>
            <div className="overline">{t("installApp.modalOverline")}</div>
            <h3 className="font-display text-xl font-bold mt-1">{t("installApp.modalTitle")}</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#0B111D]" data-testid="close-install-help"><X size={18}/></button>
        </div>
        <div className="p-5 space-y-4 text-sm">
          {isIos ? (
            <>
              <div className="font-semibold flex items-center gap-2"><Apple size={18} className="text-[#E2E8F0]"/> {t("installApp.iosHeading")}</div>
              <ol className="space-y-3 list-decimal list-inside text-[#E2E8F0]">
                <li>{t("installApp.iosStep1")}</li>
                <li>{t("installApp.iosStep2")}</li>
                <li>{t("installApp.iosStep3")}</li>
                <li>{t("installApp.iosStep4")}</li>
              </ol>
              <div className="bg-[#0E2230] border-l-2 border-[#00BFFF] p-3 text-xs text-[#94A3B8]">
                {t("installApp.iosWarn")}
              </div>
            </>
          ) : isAndroid ? (
            <>
              <div className="font-semibold flex items-center gap-2"><Smartphone size={18} className="text-[#E2E8F0]"/> {t("installApp.androidHeading")}</div>
              <div className="bg-[#2A1416] border-l-2 border-[#EF4444] p-3 text-xs">
                {t("installApp.androidWarn")}
              </div>
              <ol className="space-y-3 list-decimal list-inside text-[#E2E8F0]">
                <li>{t("installApp.androidStep1")}</li>
                <li>{t("installApp.androidStep2")}</li>
                <li>{t("installApp.androidStep3")}</li>
                <li>{t("installApp.androidStep4")}</li>
                <li>{t("installApp.androidStep5")}</li>
              </ol>
              <div className="bg-[#0B111D] p-3 text-xs text-[#94A3B8]">
                {t("installApp.androidTip")}
              </div>
            </>
          ) : (
            <>
              <div className="font-semibold">{t("installApp.genericHeading")}</div>
              <p className="text-[#94A3B8]">{t("installApp.genericDesc")}</p>
              <div className="space-y-3">
                <div>
                  <div className="font-semibold flex items-center gap-2 mb-2"><Apple size={16}/> {t("installApp.genericIos")}</div>
                  <div className="text-[#94A3B8] text-xs">{t("installApp.genericIosStep")}</div>
                </div>
                <div>
                  <div className="font-semibold flex items-center gap-2 mb-2"><Smartphone size={16}/> {t("installApp.genericAndroid")}</div>
                  <div className="text-[#94A3B8] text-xs">{t("installApp.genericAndroidStep")}</div>
                </div>
              </div>
            </>
          )}
        </div>
        <div className="p-5 border-t border-[#1E293B]">
          <button onClick={onClose} className="btn-primary w-full">{t("installApp.gotIt")}</button>
        </div>
      </div>
    </div>
  );
}
